import { computed, onUnmounted, ref, type Ref } from 'vue';
import type { TargetRect } from './useTargetRect';

const GAP = 16;
const MARGIN = 12;

/** Abaixo disto o balão vira uma faixa: não há largura para ficar ao lado do alvo. */
const NARROW_VIEWPORT = 560;

type Side = 'below' | 'above' | 'right' | 'left';

export interface Box {
	top: number;
	left: number;
	width: number;
	height: number;
}

function intersects(a: Box, b: Box): boolean {
	return (
		a.left < b.left + b.width &&
		a.left + a.width > b.left &&
		a.top < b.top + b.height &&
		a.top + a.height > b.top
	);
}

/** Quanto dois retângulos se sobrepõem. Serve de critério quando nada encaixa. */
function overlapArea(a: Box, b: Box): number {
	const w = Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left);
	const h = Math.min(a.top + a.height, b.top + b.height) - Math.max(a.top, b.top);

	return w > 0 && h > 0 ? w * h : 0;
}

function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(value, max));
}

/**
 * Onde encaixar o balão do passo, sem tapar o elemento que ele está apontando.
 *
 * Mede o balão de verdade em vez de estimar a altura dele. A versão anterior chutava
 * 260px; quando o texto passava disso — o que acontece nos passos com dica e aviso —
 * a conta de "acima do alvo" dava um valor que ainda cobria o botão, e o balão
 * aparecia exatamente em cima do que a pessoa precisava clicar.
 *
 * A escolha é por encaixe, não por preferência fixa: testa os quatro lados na ordem
 * que o passo prefere e fica no primeiro que cabe na tela E não encosta no alvo. Se
 * nenhum couber — alvo enorme, como o assistente inteiro — fica no que sobrepõe menos.
 */
export function useBubblePosition(
	rect: Ref<TargetRect | null>,
	placement: Ref<'auto' | 'top-start' | undefined>,
) {
	const bubble = ref<HTMLElement | null>(null);
	const size = ref({ width: 0, height: 0 });

	let observer: ResizeObserver | undefined;

	function measure() {
		const el = bubble.value;

		if (!el) return;

		const box = el.getBoundingClientRect();

		// Só registra mudança real, senão o observer se realimenta a cada reposicionamento.
		if (
			Math.abs(box.width - size.value.width) > 1 ||
			Math.abs(box.height - size.value.height) > 1
		) {
			size.value = { width: box.width, height: box.height };
		}
	}

	/** Chamado pelo componente via `ref`, quando o balão entra e sai do DOM. */
	function setBubble(el: HTMLElement | null) {
		bubble.value = el;

		observer?.disconnect();
		observer = undefined;

		if (!el) return;

		if (typeof ResizeObserver !== 'undefined') {
			observer = new ResizeObserver(measure);
			observer.observe(el);
		}

		measure();
	}

	onUnmounted(() => observer?.disconnect());

	/** Enquanto não mediu, o balão fica invisível — evita o pulo do reposicionamento. */
	const measured = computed(() => size.value.height > 0);

	const style = computed(() => {
		const target = rect.value;

		if (!target) return null;

		const position = computeBubblePosition(
			target,
			{ width: size.value.width || 380, height: size.value.height || 240 },
			{ width: window.innerWidth, height: window.innerHeight },
			placement.value,
		);

		return position.full
			? `top: ${position.top}px; left: ${MARGIN}px; right: ${MARGIN}px; width: auto;`
			: `top: ${position.top}px; left: ${position.left}px;`;
	});

	return { setBubble, style, measured };
}

export interface BubblePosition {
	top: number;
	left: number;
	/** Tela estreita: o balão ocupa a largura toda, entre as margens. */
	full: boolean;
}

/**
 * A geometria, isolada do DOM para poder ser testada em todas as combinações.
 *
 * Garante a única regra que importa: o balão não fica em cima do alvo sempre que
 * houver qualquer lugar onde ele caiba.
 */
export function computeBubblePosition(
	target: TargetRect,
	size: { width: number; height: number },
	viewport: { width: number; height: number },
	placement?: 'auto' | 'top-start',
): BubblePosition {
	{
		const width = size.width;
		const height = size.height;
		const vw = viewport.width;
		const vh = viewport.height;

		const box = (top: number, left: number): Box => ({ top, left, width, height });

		// Tela estreita: não há espaço lateral, então o balão vira faixa e vai para o
		// lado do alvo que tem mais folga — acima ou abaixo, nunca em cima.
		if (vw < NARROW_VIEWPORT) {
			const below = target.top + target.height + GAP;
			const above = target.top - height - GAP;
			const roomBelow = vh - (target.top + target.height);
			const roomAbove = target.top;

			const top =
				roomBelow >= height + GAP
					? below
					: roomAbove >= height + GAP
						? above
						: roomBelow >= roomAbove
							? vh - height - MARGIN
							: MARGIN;

			return {
				top: clamp(top, MARGIN, Math.max(MARGIN, vh - height - MARGIN)),
				left: MARGIN,
				full: true,
			};
		}

		// Alinhamentos: abaixo/acima acompanham a borda do alvo; ao lado, o topo dele.
		const alignedLeft = clamp(target.left, MARGIN, Math.max(MARGIN, vw - width - MARGIN));
		const alignedRight = clamp(
			target.left + target.width - width,
			MARGIN,
			Math.max(MARGIN, vw - width - MARGIN),
		);
		const alignedTop = clamp(target.top, MARGIN, Math.max(MARGIN, vh - height - MARGIN));

		const candidates: Record<Side, Box> = {
			below: box(target.top + target.height + GAP, alignedLeft),
			// Acima alinha pela direita: é o encaixe que o passo do botão de nova
			// ocorrência pede, com o balão crescendo para a esquerda.
			above: box(target.top - height - GAP, alignedRight),
			right: box(alignedTop, target.left + target.width + GAP),
			left: box(alignedTop, target.left - width - GAP),
		};

		const order: Side[] =
			placement === 'top-start'
				? ['above', 'left', 'right', 'below']
				: ['below', 'above', 'right', 'left'];

		const targetBox: Box = {
			top: target.top,
			left: target.left,
			width: target.width,
			height: target.height,
		};

		const fits = (c: Box) =>
			c.top >= MARGIN &&
			c.top + c.height <= vh - MARGIN &&
			c.left >= MARGIN &&
			c.left + c.width <= vw - MARGIN &&
			!intersects(c, targetBox);

		const chosen = order.map((side) => candidates[side]).find(fits);

		if (chosen) return { top: chosen.top, left: chosen.left, full: false };

		// Nada coube sem encostar — alvo muito grande, como o assistente inteiro. Fica
		// onde tapa menos, sempre dentro da tela.
		const fallback = order
			.map((side) => {
				const c = candidates[side];

				return box(
					clamp(c.top, MARGIN, Math.max(MARGIN, vh - height - MARGIN)),
					clamp(c.left, MARGIN, Math.max(MARGIN, vw - width - MARGIN)),
				);
			})
			.reduce((best, c) => (overlapArea(c, targetBox) < overlapArea(best, targetBox) ? c : best));

		return { top: fallback.top, left: fallback.left, full: false };
	}
}
